<template>
    <div
        class="calendar-list-item"
        @click="() => onDayClick()"
        data-cy="calendar day item"
        v-bind:data-cy-date="kapitelDate"
    >
        <div
            class="date"
            :class="{'today': dateHelper.isToday(kapitelDate), 'weekend': dateHelper.isWeekend(kapitelDate) }"
        >
            <span>
                <strong>{{ dateHelper.getDayOfMonthFromKapitelDate(kapitelDate) }}</strong>
            </span>
            <span>
                {{ dateHelper.formatWeekdayShortFromKapitelDate(kapitelDate, false) }}
            </span>
        </div>
        <div
            class="body"
        >
            <Skeleton :items="['block--height-5.0rem']" v-if="propsLoading"></Skeleton>
            <template v-else>
            <div class="shifts" v-if="dayItemType !== 'empty'">
                <span
                    v-for="shift in orderedShiftTypes"
                    :key="shift"
                    :class="{'booked': shiftStates[shift].isBooked, 'absence': shiftStates[shift].isAbsent, 'available': shiftStates[shift].isAvailable, 'suggested': shiftStates[shift].isSuggested, 'wishDemands': shiftStates[shift].isWish}"
                >
                    {{ shift }}
                </span>
            </div>
            <div
                class="content"
            >
                <div
                    class="content-item"
                    :class="{'available': dayItemType == 'availability', 'absence': dayItemType == 'absence', 'booked': !!props.day?.booking, 'wishDemands': dayItemType == 'wishDemands', 'text-only': 'demandSuggestions' }"
                    >
                    <div v-if="booking" class="booking">
                        <div
                            class="labels"
                        >
                            <p
                                class="text-bold"
                            >
                                {{ booking.labelJobsite }}
                            </p>
                            <p>
                                <template v-if="hasBookingWithdrawalRequest">
                                    <span class="withdrawal-request-text">Suche nach Ersatz...</span>
                                </template>
                                <template v-else>
                                    {{ booking.begin }} - {{ booking.until }} Uhr
                                </template>
                            </p>
                            <p v-if="booking.comment" style="white-space: nowrap;overflow: hidden;text-overflow: ellipsis; max-width: 150px">
                                <InlineSVG :src="KapitelIcon.comment" style="height: 10px; display: inline-flex"/> {{booking.comment}}
                            </p>
                        </div>
                        <div
                            class="image"
                        >
                            <Avatar
                                :src="booking.image"
                            />
                        </div>
                    </div>
                    <p v-else-if="dayItemType == 'absence' && props.day?.absence">
                        {{ getLabelForAbsenceType(props.day.absence.type) }}<br>
                        {{ props.day.absence.comment }}
                    </p>
                    <p v-else-if="dayItemType === 'availability' && props.day?.availability">
                        <strong>{{availabilityRS?.label}}</strong>
                        <br v-if="!availabilityRS?.hasShiftsAndAdditionalAvailabilities">
                        {{ availabilityRS?.effectiveTimes }}
                        <br v-if="availabilityRS?.hasShiftsAndAdditionalAvailabilities"/>
                        <span>{{ availabilityRS?.additionalAvailabilitySummary }}</span>

                        <template v-if="availabilityRS?.shortNoticeWarning">
                            <br/>
                            <span>{{ availabilityRS?.shortNoticeWarning }}</span>
                        </template>
                    </p>
                    <p v-else-if="dayItemType === 'demandSuggestions'">
                        {{demandSuggestionsRenderSet.label}}
                    </p>
                    <p v-else-if="dayItemType === 'wishDemands' && props.day?.wishDemands">
                        {{ pluralizeUnitOnly(props.day?.wishDemands.length, 'Zusatzdienste', 'Zusatzdienst') }} auf {{ listFormat(props.day?.wishDemands.map(ds => ds.jobsite.abbreviation ?? '')) }}
                    </p>
                </div>

                <div
                    class="content-item text-only"
                    v-if="dayItemType === 'availability' && demandSuggestionsForAvailableDay.length > 0">
                    {{demandSuggestionsForAvailableDayRenderSet.shortLabel}}
                </div>
            </div>

            <ion-action-sheet
                :is-open="isWishDemandsModalOpen"
                :buttons="actionSheetWishDemandButtons"
                @didDismiss="onActionModalWishDemandDismiss"
            ></ion-action-sheet>

            <ion-action-sheet
                :is-open="isConfirmRemoveWishDemandModalOpen"
                :buttons="actionSheetConfirmWishDemandButtons"
                @didDismiss="onActionModalConfirmWishDemandDismiss"
            ></ion-action-sheet>

            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import {useRouter} from 'vue-router'
import {
    generateAvailability,
    generatePropertiesFromDefault,
    getAvailabilityLabels,
    hasShiftsOrAAs
} from "@/helper/availability";
import {computed, PropType, Ref, ref, watch} from 'vue'
import {generateAbsence, getLabelForAbsenceType} from '@/helper/absence'
import Avatar from '@/components/Avatar.vue';
import {orderedShiftTypes} from "@/helper/shiftTypes";
import datetime from "@/helper/datetime/datetime"
import Skeleton from "@/components/Skeleton.vue";
import dateHelper from "@/helper/datetime/date";
import {getImageForJobsite, getShortJobsiteDescription} from '@/helper/jobsite';
import Modal from '@/components/Modal.vue'
import {
    AbsenceType,
    AvailabilityShiftDefault,
    BookingWithdrawalRequest,
    Day,
    Demand,
    DemandJoDaShTi,
    ShiftType
} from '@/graphql/generated/graphql';
import {ActionSheetButton, IonActionSheet} from '@ionic/vue'
import {removeWishDemand} from "@/helper/wishDemand";
import {useAppState} from "@/helper/appState";
import {CalendarDateSingleton} from "@/helper/calendar/calendarDateSingleton";
import {generateDemandSuggestionsRenderSet} from "@/helper/demand";
import {listFormat, pluralizeUnitOnly} from "../../helper/amountFormatter";
import InlineSVG from "@/components/InlineSVG.vue";
import {KapitelIcon} from "@/helper/icon";

const router = useRouter();

const emit = defineEmits(['set:wish-demand', 'click:date'])

const props = defineProps({
    calendarDateSingleton: {
        type: Object as PropType<CalendarDateSingleton>,
        required: true
    },
    day: Object as PropType<Day>,
    bookingWithdrawalRequest: {
        type: Object as PropType<BookingWithdrawalRequest | undefined>,
        required: false,
        default: undefined
    },
    availabilityShiftDefaults: Array as PropType<AvailabilityShiftDefault[]>,
    propsLoading: {
        type: Boolean,
        default: false
    }
})

const kapitelDate = computed(() => props.calendarDateSingleton.date )

const dayItemType = computed(()=>{

    if(props.day?.booking){
        return "booking"
    }else if(props.day?.absence){
        return "absence"
    }else if (props.day?.availability &&  hasShiftsOrAAs(props.day?.availability)){
        return "availability"
    } else if(props.day?.moreMoreDemands && props.day?.moreMoreDemands.length>0) {
        return "demandSuggestions"
    } else if(props.day?.wishDemands && props.day?.wishDemands.length>0){
        return "wishDemands"
    }
    return "empty"
})

const shiftStates = computed(() => {
    return orderedShiftTypes.reduce((acc, shift) => {
        const isBooked = dayItemType.value === 'booking' && props.day?.booking?.shiftType === shift
        const isAbsent = dayItemType.value === 'absence' && !!props.day?.absence
        const isAvailable = dayItemType.value === 'availability' && !isBooked && !isAbsent && (
            props.day?.availability?.shifts.some(sh => sh.type === shift)
            || (props.day?.availability?.additionalAvailabilityCoordinates && props.day?.availability?.additionalAvailabilityCoordinates.some(sh => sh.shiftType === shift))
        )
        const isSuggested = (
            !isBooked &&
            !isAbsent &&
            (
                (dayItemType.value !== 'availability' && props.day?.moreMoreDemands && props.day?.moreMoreDemands?.some((d)=>d.shiftType === shift)) ||
                (dayItemType.value === 'availability' && demandSuggestionsForAvailableDay.value.some((d)=>d.shiftType === shift))
            )
        )
        const isWish = !isBooked && !isAbsent && !isAvailable && !isSuggested && props.day?.wishDemands?.some((d)=>d.shiftType === shift)

        acc[shift] = {
            isBooked: isBooked,
            isAbsent: isAbsent,
            isAvailable: !!isAvailable,
            isSuggested: isSuggested,
            isWish: !!isWish
        }

        return acc
    }, {} as {[key in ShiftType]: {isBooked:boolean,isAbsent:boolean,isAvailable:boolean,isSuggested:boolean,isWish:boolean}})
})

const demandSuggestionsForAvailableDay = computed(()  => props.day ? props.day.moreMoreDemands : [])
const demandSuggestionsRenderSet = computed(() => generateDemandSuggestionsRenderSet(props.day?.moreMoreDemands ?? [], 3))
const demandSuggestionsForAvailableDayRenderSet = computed(() => generateDemandSuggestionsRenderSet(demandSuggestionsForAvailableDay.value))

const isWishDemandsModalOpen = ref(false)
const isConfirmRemoveWishDemandModalOpen = ref(false)
const toDeleteWishDemand: Ref<Demand|undefined> = ref(undefined)
//
// watch(() => props.modelValue, value => {
//     isWishDemandsModalOpen.value = value
//     isConfirmRemoveWishDemandModalOpen.value = value
// })


const availabilityRS = computed(() => {
    if (!props.day?.availability) {
        return undefined
    }
    return getAvailabilityLabels(props.day.availability)
})

const booking = computed(() => {
    if (!props.day?.booking) {
        return
    }

    return {
        labelJobsite: getShortJobsiteDescription(props.day?.booking.jobsite, false, ' - '),
        begin: datetime.formatKapitelDateTime(props.day?.booking.begin, datetime.kapitelDateTimeFormatTime),
        until: datetime.formatKapitelDateTime(props.day?.booking.until, datetime.kapitelDateTimeFormatTime),
        image: getImageForJobsite(props.day?.booking.jobsite),
        comment: props.day?.booking.comment?.text,
    }
})

const hasBookingWithdrawalRequest = computed(() => {
    return !!props.bookingWithdrawalRequest
})

const onDayClick = () => {
    if(useAppState().isEmployeeOrCandidate('candidate')){
        const wish = dayItemType.value == "wishDemands"
        if(wish) {
            isWishDemandsModalOpen.value = true
        }
    } else if (useAppState().isEmployeeOrCandidate('employee')) {
        emit('click:date', { dateId: kapitelDate.value, expectedContent: dayItemType.value })
    }

}

const actionSheetWishDemandButtons = (computed(() => {

    const wishDemands = [] as ActionSheetButton[];

    props.day?.wishDemands?.forEach((demand) => {
        wishDemands.push({
            text: demand.shiftType + " auf " + demand.jobsite.name + " entfernen",
            data: {
                action: "removeWishDemand",
                demand: demand
            },
        });
    });

    return ([] as ActionSheetButton[]).concat(wishDemands, [
        {
            text: "Abbrechen",
            role: "cancel",
            data: {
                action: "cancel",
            },
        }
    ])
}));

const actionSheetConfirmWishDemandButtons = [
    {
        text: "Ja, Zusatzdienstwunsch entfernen",
        role: 'destructive',
        data: {
            action: "removeWishDemand",
        },
    },
    {
        text: "Abbrechen",
        role: "cancel",
        data: {
            action: "cancel",
        },
    },
];

const onActionModalConfirmWishDemandDismiss = async (event: any) => {
    const data = event.detail.data;

    if(!data?.action){
        isConfirmRemoveWishDemandModalOpen.value = false
        toDeleteWishDemand.value = undefined
        return
    }

    switch (data.action) {
        case "removeWishDemand":
            if (toDeleteWishDemand.value) {
                removeWishDemand(toDeleteWishDemand.value)
                emit("set:wish-demand", toDeleteWishDemand.value)
            }
            break;
    }

    isConfirmRemoveWishDemandModalOpen.value = false
    toDeleteWishDemand.value = undefined
};

const onActionModalWishDemandDismiss = async (event:any) => {
    const data = event.detail.data;

    if(!data?.action){
        isWishDemandsModalOpen.value = false
        return
    }

    switch (data.action) {
        case "removeWishDemand":
            toDeleteWishDemand.value = data.demand
            isConfirmRemoveWishDemandModalOpen.value = true
            break;
    }

    isWishDemandsModalOpen.value = false
};


</script>

<style lang="scss">
.calendar-list-item {
    display: flex;
    align-items: flex-start;
    margin-top: 1rem;
    margin-bottom: 1rem;
    margin-right: var(--custom-spacing-app-content-padding-horizontal);

    min-height: 5.5rem; // reducing jumpiness with skeleton loader

    > .date {
        flex: 0 0 5rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;

        > span:first-child {
            font-size: var(--custom-font-size-large);
            color: var(--ion-color-dark);
            margin-bottom: 0.15em;
        }

        > span:last-child {
            font-size: var(--custom-font-size-small);
            color: var(--ion-color-medium);
        }

        &.today {
            > span:first-child {
                color: var(--ion-color-primary);
            }
        }

        &.weekend {
            > span:last-child {
                color: var(--ion-color-primary);
            }
        }
    }

    > .body {
        display: flex;
        flex: 1 1 100%;

        > .shifts {
            flex: 0 0 auto;
            margin-right: 0.5rem;
            display: flex;
            flex-direction: column;

            > span {
                color: var(--ion-color-grey-tint-1);
                font-size: var(--custom-font-size-extrasmall);
                font-weight: var(--custom-font-weight-bold);
                display: flex;
                align-items: center;
                justify-content: center;
                width: 2.2em;
                height: 2.2em;
                border-radius: 50%;
                margin-bottom: 0.2em;

                &.available {
                    background-color: rgba(var(--ion-color-green-rgb), 0.2);
                    color: var(--ion-color-dark);
                }

                &.wishDemands {
                    background-color: var(--ion-color-light);
                    color: var(--ion-color-dark);
                }

                &.suggested {
                    /* background-color: var(--ion-color-light); */
                    color: var(--ion-color-dark);
                }


                &.booked {
                    background-color: var(--ion-color-dark);
                    color: var(--ion-color-white);
                }
            }
        }

        > .content {
            flex: 1 1 100%;

            > .content-item {
                width: 100%;
                border-radius: 1rem;
                padding: 1rem;
                display: flex;
                align-items: center;

                &:only-child {
                    min-height: 100%;
                }
                
                &:not(:last-child) {
                    margin-bottom: 0.5em;
                }

                &,
                * {
                    font-size: var(--custom-font-size-regular);
                }

                &.text-only {
                    padding-top: 0;
                    padding-bottom: 0;
                }

                &.available {
                    background-color: rgba(var(--ion-color-green-rgb), 0.1);
                    color: var(--ion-color-green-shade)
                }

                &.absence {
                    background-color: rgba(var(--ion-color-danger-rgb), 0.1);
                    color: var(--ion-color-danger);
                }

                &.booked {
                    background-color: var(--ion-color-dark);
                    color: var(--ion-color-white);

                    > .booking {
                        width: 100%;
                        display: flex;
                        align-items: center;

                        min-width: 0;

                        > .labels {
                            flex: 1 1 100%;
                            margin-right: 1rem;
                        }

                        > .image {
                            flex: 0 0 auto;
                        }
                    }
                }

                &.wishDemands {
                    background-color: var(--ion-color-light);
                }
            }            
        }
    }
}

.withdrawal-request-text {
    color: var(--ion-color-danger);
}
</style>
